import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Input } from '../../../components/ui/Input';
import { 
  CheckCircle, 
  Search, 
  User, 
  MessageSquare, 
  MoreHorizontal,
  Eye,
  Edit,
  Archive,
  Star,
  Calendar,
  Tag,
  TrendingUp,
  TrendingDown,
  Users,
  RefreshCw,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Ticket,
  Filter,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Pen,
  Clock,
  AlertCircle,
  Download
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../../utils/exportStyledXlsx';
import { ticketService } from '../../../api/ticketService';
import { usePriorities } from '../../../hooks/usePriorities';
import { getCurrentUser } from '../../../api/authService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { toast } from 'react-hot-toast';

const ClosedTickets = () => {
  const navigate = useNavigate();
  const [closedTickets, setClosedTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading state for search/filter operations
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('closed');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false
  });
  const { data: priorities = [], isSuccess: prioritiesReady } = usePriorities();
  const [teamAgents, setTeamAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('all');
  const [isTeamLead, setIsTeamLead] = useState(null); // null = not determined yet
  const [roleDetermined, setRoleDetermined] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Ref to preserve search input focus
  const searchInputRef = useRef(null);

  // Check if user is Team Lead
  useEffect(() => {
    const user = getCurrentUser();
    const isLead = user?.roleCode === 'TEAM_LEAD' || user?.role === 'team-lead';
    setIsTeamLead(isLead);
    setRoleDetermined(true);
    
    if (isLead) {
      loadTeamAgents();
    }
  }, []);

  const loadTeamAgents = async () => {
    try {
      const response = await ticketService.getTeamAgents();
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const agents = Array.isArray(responseData.data) ? responseData.data : [];
        setTeamAgents(agents);
      } else {
        console.warn('Failed to load team agents:', responseData?.message);
        setTeamAgents([]);
      }
    } catch (err) {
      console.error('Error loading team agents:', err);
      setTeamAgents([]);
    }
  };

  const loadClosedTickets = React.useCallback(async (isSearchOperation = false) => {
    // Use searchLoading for search/filter operations to avoid full page reload
    if (isSearchOperation) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      // Use priorities from state (should already be loaded on mount)
      const availablePriorities = priorities.length > 0 ? priorities : [];
      
      // Double-check role if not determined yet
      let currentIsTeamLead = isTeamLead;
      if (!roleDetermined) {
        const user = getCurrentUser();
        currentIsTeamLead = user?.roleCode === 'TEAM_LEAD' || user?.role === 'team-lead';
      }
      
      // Build filter parameters
      const fetchParams = { 
        page: currentPage, 
        size: pageSize 
      };
      
      // Add search parameter
      if (searchTerm && searchTerm.trim()) {
        fetchParams.search = searchTerm.trim();
      }
      
      // Add priority filter
      if (priorityFilter !== 'all' && priorityFilter) {
        const priorityObj = availablePriorities.find(p => 
          (p.priorityCode || p.priorityName || '').toLowerCase() === priorityFilter.toLowerCase() ||
          p.priorityId?.toString() === priorityFilter
        );
        if (priorityObj?.priorityId) {
          fetchParams.priorityId = priorityObj.priorityId;
        }
      }
      
      // Add date filters - convert to ISO format for backend (LocalDateTime format: YYYY-MM-DDTHH:mm:ss)
      if (startDate) {
        // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
        const startDateTime = new Date(startDate);
        startDateTime.setHours(0, 0, 0, 0); // Set to start of day
        const year = startDateTime.getFullYear();
        const month = String(startDateTime.getMonth() + 1).padStart(2, '0');
        const day = String(startDateTime.getDate()).padStart(2, '0');
        fetchParams.startDate = `${year}-${month}-${day}T00:00:00`;
      }
      if (endDate) {
        // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999); // Set to end of day
        const year = endDateTime.getFullYear();
        const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
        const day = String(endDateTime.getDate()).padStart(2, '0');
        fetchParams.endDate = `${year}-${month}-${day}T23:59:59`;
      }
      
      // Add status filter (for closed tickets, can filter between 'closed' and 'resolved')
      if (statusFilter !== 'all' && statusFilter) {
        fetchParams.statusCode = statusFilter;
      }
      
      // Fetch closed tickets - use new dedicated endpoint for Team Leads
      let response;
      if (currentIsTeamLead) {
        // Add agentId filter if agent is selected
        if (agentFilter !== 'all') {
          fetchParams.agentId = parseInt(agentFilter);
        }
        
        response = await ticketService.getTeamClosedTicketsList(fetchParams);
      } else {
        // For Agents, use the regular closed endpoint
        response = await ticketService.getClosedTickets(fetchParams);
      }
      
      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];
        
        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets.map(ticket => {
          // Map priorityId to priority name/code
          let priority = 'medium';
          let priorityName = 'Medium';
          let priorityId = ticket.priorityId;
          
          if (priorityId && availablePriorities.length > 0) {
            const priorityObj = availablePriorities.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
            if (priorityObj) {
              priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
              priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
            }
          } else if (ticket.priority) {
            // Fallback to ticket.priority if available
            priority = ticket.priority.toLowerCase();
            priorityName = ticket.priority;
          }
          
          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketId: ticket.ticketId, // Store ticketId for feedback lookup
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'closed',
            priority: priority,
            priorityName: priorityName,
            priorityId: priorityId,
            category: ticket.category || 'Uncategorized',
            categoryId: ticket.categoryId,
            customer: {
              name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
              email: ticket.requesterEmail || '',
              company: ''
            },
            assignedAgent: ticket.assigneeId ? `Agent ${ticket.assigneeId}` : 'Unassigned',
            assignedTime: ticket.assignedTime,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            closedDate: ticket.closedAt || ticket.resolvedAt || ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.resolvedAt || ticket.updatedAt,
            tags: [],
            attachments: ticket.attachmentCount || 0,
            comments: ticket.replyCount || 0,
            slaStatus: ticket.slaIsBreached ? 'breached' : 'met',
            slaRemainingMinutes: ticket.slaRemainingMinutes,
            slaElapsedMinutes: ticket.slaElapsedMinutes,
            slaIsBreached: ticket.slaIsBreached,
            slaIsEscalated: ticket.slaIsEscalated,
            timeSpent: '0h 0m',
            estimatedTime: '0h',
            lastActivity: ticket.updatedAt || ticket.createdAt,
            // Location fields
            countryId: ticket.countryId,
            countyId: ticket.countyId,
            subCountyId: ticket.subCountyId,
            wardId: ticket.wardId,
            countryName: ticket.countryName,
            countyName: ticket.countyName,
            subCountyName: ticket.subCountyName,
            wardName: ticket.wardName,
            feedback: ticket.feedback || null
          };
        });
        
        setClosedTickets(transformedTickets);
        // For server-side pagination, filteredTickets should only contain current page tickets
        // Client-side filtering will be applied on the current page's tickets
        setFilteredTickets(transformedTickets);
        
        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || currentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (currentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });
      } else {
        setError(responseData?.message || 'Failed to load closed tickets');
        toast.error(responseData?.message || 'Failed to load closed tickets');
        setClosedTickets([]);
        setFilteredTickets([]);
      }
    } catch (err) {
      console.error('Error loading closed tickets:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load closed tickets';
      setError(errorMessage);
      toast.error(errorMessage);
      setClosedTickets([]);
      setFilteredTickets([]);
    } finally {
      if (isSearchOperation) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [isTeamLead, agentFilter, roleDetermined, currentPage, pageSize, priorities, searchTerm, priorityFilter, startDate, endDate, statusFilter]);

  const prevFiltersKeyRef = useRef(null);

  // Single coordinated load: role ready, priorities loaded, page/filters/search
  useEffect(() => {
    if (!roleDetermined || !prioritiesReady || priorities.length === 0) return;

    const filtersKey = JSON.stringify({
      searchTerm,
      priorityFilter,
      startDate,
      endDate,
      statusFilter,
      agentFilter
    });
    const filtersChanged =
      prevFiltersKeyRef.current !== null && prevFiltersKeyRef.current !== filtersKey;
    prevFiltersKeyRef.current = filtersKey;

    if (filtersChanged && currentPage !== 0) {
      setCurrentPage(0);
      return;
    }

    const isSearch = Boolean(searchTerm && searchTerm.trim());
    const delay = isSearch ? 500 : 0;
    const timeoutId = setTimeout(() => {
      loadClosedTickets(isSearch);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [
    roleDetermined,
    priorities.length,
    currentPage,
    searchTerm,
    priorityFilter,
    startDate,
    endDate,
    statusFilter,
    agentFilter,
    loadClosedTickets
  ]);

  const handleViewTicket = (ticketId) => {
    // Navigate with view mode (read-only) like TeamTickets
    navigate(`/agent/tickets/${ticketId}`, { 
      state: { mode: 'view', openComments: true, scrollToComments: true } 
    });
  };

  const handleEditTicket = (ticketId) => {
    // Navigate with edit mode - allows editing
    navigate(`/agent/tickets/${ticketId}`, { 
      state: { mode: 'edit' } 
    });
  };

  // Client-side sorting only (server handles filtering)
  useEffect(() => {
    if (closedTickets.length > 0) {
      let sorted = [...closedTickets];
      
      // Sort tickets
      sorted.sort((a, b) => {
        switch (sortBy) {
          case 'closed':
            return new Date(b.closedDate || b.updatedAt) - new Date(a.closedDate || a.updatedAt);
          case 'created':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'updated':
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          case 'priority':
            const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          default:
            return 0;
        }
      });
      
      setFilteredTickets(sorted);
    }
  }, [closedTickets, sortBy]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'closed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Ticket className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track':
        return 'text-green-600';
      case 'at-risk':
        return 'text-yellow-600';
      case 'breached':
        return 'text-red-600';
      case 'completed':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return 'text-white bg-red-800 dark:bg-red-900';
      case 'critical': return 'text-red-800 bg-red-100 dark:text-red-200 dark:bg-red-900/50';
      case 'high': return 'text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/40';
      case 'medium': return 'text-yellow-800 bg-yellow-100 dark:text-yellow-200 dark:bg-yellow-900/40';
      case 'low': return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40';
      default: return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
    }
  };

  // Get light background color for ticket row based on priority
  const getPriorityBackgroundColor = (priority) => {
    // Return default background - no priority-based colors
    return 'bg-white dark:bg-gray-800';
  };

  // Get darker border color for ticket row based on priority
  const getPriorityBorderColor = (priority) => {
    return '';
  };

  const getStatusColor = (status) => {
    switch (String(status || '').toLowerCase().replace(/[_-]/g, ' ')) {
      case 'closed':
      case 'resolved': return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/40';
      case 'open':
      case 'new': return 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/40';
      default: return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700';
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'N/A';
    return String(status).replace(/[_-]/g, ' ').toUpperCase();
  };

  const handleRefresh = () => {
    loadClosedTickets();
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setStartDate('');
    setEndDate('');
    setAgentFilter('all');
    toast.success('All filters cleared');
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const fetchClosedForExport = async () => {
    const availablePriorities = priorities.length > 0 ? priorities : [];
    let currentIsTeamLead = isTeamLead;
    if (!roleDetermined) {
      const user = getCurrentUser();
      currentIsTeamLead = user?.roleCode === 'TEAM_LEAD' || user?.role === 'team-lead';
    }
    const fetchParams = { fetchAll: true };
    if (searchTerm?.trim()) fetchParams.search = searchTerm.trim();
    if (priorityFilter !== 'all' && priorityFilter) {
      const priorityObj = availablePriorities.find(p =>
        (p.priorityCode || p.priorityName || '').toLowerCase() === priorityFilter.toLowerCase() ||
        p.priorityId?.toString() === priorityFilter
      );
      if (priorityObj?.priorityId) fetchParams.priorityId = priorityObj.priorityId;
    }
    if (startDate) {
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0);
      fetchParams.startDate = `${startDateTime.getFullYear()}-${String(startDateTime.getMonth() + 1).padStart(2, '0')}-${String(startDateTime.getDate()).padStart(2, '0')}T00:00:00`;
    }
    if (endDate) {
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);
      fetchParams.endDate = `${endDateTime.getFullYear()}-${String(endDateTime.getMonth() + 1).padStart(2, '0')}-${String(endDateTime.getDate()).padStart(2, '0')}T23:59:59`;
    }
    if (statusFilter !== 'all' && statusFilter) fetchParams.statusCode = statusFilter;
    if (currentIsTeamLead && agentFilter !== 'all') fetchParams.agentId = parseInt(agentFilter);

    // Role-aware /my-tickets/closed?fetchAll=true (unpaged). Team lead uses same endpoint.
    const response = await ticketService.getClosedTickets(fetchParams);
    const content = response?.data?.data?.content || [];
    return (content || []).map((ticket) => {
      let priorityName = 'Medium';
      const priorityId = ticket.priorityId;
      if (priorityId && availablePriorities.length > 0) {
        const p = availablePriorities.find(pr => pr.priorityId === priorityId || String(pr.priorityId) === String(priorityId));
        if (p) priorityName = p.priorityName || p.priorityCode || 'Medium';
      } else if (ticket.priority) priorityName = ticket.priority;
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        category: ticket.category || 'Uncategorized',
        priorityName,
        status: (ticket.status || 'closed').toLowerCase(),
        closedDate: ticket.closedAt || ticket.resolvedAt || ticket.updatedAt || ticket.createdAt
      };
    });
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchClosedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Category', 'Priority', 'Status', 'Closed Date'];
      const excelRows = rows.map((r, i) => [
        i + 1, r.ticketNumber, r.subject, r.category, r.priorityName, r.status, r.closedDate || '',
      ]);
      downloadStyledXlsx({
        fileName: `closed-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Closed',
        headers,
        rows: excelRows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4, 5],
        dateColumns: [6],
        centerColumns: [0, 4, 5, 6],
      });
      toast.success('Tickets exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchClosedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Closed Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1, r.ticketNumber, (r.subject || '').substring(0, 35), (r.category || '').substring(0, 15), r.priorityName, r.status, formatDateTimeExport(r.closedDate)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket Number', 'Subject', 'Category', 'Priority', 'Status', 'Closed Date']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`closed-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Closed Tickets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            View resolved and closed tickets with performance metrics
          </p>
        </div>
        <div className="flex gap-3 flex-nowrap">
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportExcel}
                  >
                    <FileText className="h-4 w-4" />
                    Export Excel
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={handleToggleFilters}
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <div>
            <h4 className="font-semibold">Error loading closed tickets</h4>
            <p className="text-sm">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadClosedTickets}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}


      {/* Search Bar - Always visible above the table */}
      <Card key="search-bar-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              key="search-input"
              type="text"
              placeholder="Search tickets by subject, description, or ticket number..."
              value={searchTerm}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className="pl-10 pr-4 py-2.5 w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">

              {/* Status Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    e.preventDefault();
                    setStatusFilter(e.target.value);
                  }}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Status</option>
                  <option value="closed">Closed</option>
                  <option value="resolved">Resolved</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    e.preventDefault();
                    setPriorityFilter(e.target.value);
                  }}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Priority</option>
                  {priorities.map((priority) => (
                    <option key={priority.priorityId} value={priority.priorityId}>
                      {priority.priorityName || priority.priorityCode}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Start Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setStartDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                />
              </div>

              {/* End Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setEndDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                />
              </div>

              {/* Agent Filter (for Team Leads) */}
              {isTeamLead && (
                <div className="relative flex-shrink-0">
                  <select
                    value={agentFilter}
                    onChange={(e) => {
                      e.preventDefault();
                      setAgentFilter(e.target.value);
                    }}
                    className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                  >
                    <option value="all">All Agents</option>
                    {teamAgents.map((agent) => (
                      <option key={agent.userId || agent.id} value={agent.userId || agent.id}>
                        {agent.fullName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              )}

              {/* Sort By Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="closed">Closed Date</option>
                  <option value="updated">Last Updated</option>
                  <option value="created">Created Date</option>
                  <option value="priority">Priority</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Apply
                </Button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets List - Always render, show skeleton during initial load */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
        {searchLoading && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-blue-600 animate-pulse z-10"></div>
        )}
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading closed tickets...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Feedback
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Closed Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket, index) => {
                  return (
                    <tr 
                      key={ticket.id} 
                      className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {currentPage * pageSize + index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleViewTicket(ticket.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleViewTicket(ticket.id);
                            }
                          }}
                          className="flex items-center min-w-0 w-full text-left cursor-pointer hover:opacity-80 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          title="View ticket"
                        >
                          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Ticket className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {ticket.ticketNumber || ticket.id}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ticket.title || ticket.subject}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-md ${getPriorityColor(ticket.priority || 'medium')}`}>
                          {(ticket.priorityName || ticket.priority || 'medium').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-md ${getStatusColor(ticket.status)}`}>
                          {formatStatusLabel(ticket.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {(() => {
                          const feedback = ticket.feedback;
                          
                          if (feedback) {
                            const rating = feedback.starsCount || feedback.rating || 0;
                            const comment = feedback.comment || '';
                            
                            return (
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center space-x-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      className={`h-3 w-3 ${
                                        star <= rating
                                          ? 'fill-yellow-400 text-yellow-400'
                                          : 'text-gray-300 dark:text-gray-600'
                                      }`}
                                    />
                                  ))}
                                  <span className="text-xs text-gray-600 dark:text-gray-400 ml-1">
                                    ({rating}/5)
                                  </span>
                                </div>
                                {comment && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs" title={comment}>
                                    {comment}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          return (
                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                              No feedback
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(ticket.closedDate || ticket.updatedAt)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatTime(ticket.closedDate || ticket.updatedAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex items-center gap-1.5"
                            onClick={() => handleEditTicket(ticket.id)}
                          >
                            <Pen className="w-4 h-4" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex items-center gap-1.5"
                            onClick={() => handleViewTicket(ticket.id)}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination */}
            {paginationData && paginationData.totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                      <span className="font-medium">{Math.min((currentPage + 1) * pageSize, paginationData.totalElements)}</span> of{' '}
                      <span className="font-medium">{paginationData.totalElements}</span> results
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                      disabled={currentPage === 0 || paginationData.first}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                      let pageNum;
                      if (paginationData.totalPages <= 5) {
                        pageNum = i;
                      } else if (currentPage < 3) {
                        pageNum = i;
                      } else if (currentPage > paginationData.totalPages - 4) {
                        pageNum = paginationData.totalPages - 5 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 text-sm border rounded-md ${
                            pageNum === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages - 1))}
                      disabled={currentPage >= paginationData.totalPages - 1 || paginationData.last}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Empty State */}
      {!loading && filteredTickets.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No closed tickets found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search criteria'
                : 'No tickets have been closed yet'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClosedTickets;
